import { Permission } from './permissions.js'

/**
 * The five built-in role names, lowest authority first. Persisted in
 * `users_roles.name` as `varchar(64)`; the same name may exist once platform-wide
 * (`tenant_id IS NULL`) and once per tenant — enforced by the `(name, tenant_id)`
 * composite unique index.
 *
 * Roles differ by permission bundle and default scope (`ROLE_DEFAULT_SCOPE`),
 * never by tree level: the level comes from the org unit(s) a user is placed
 * in (TO-388). `ORG_USER` and `ORG_MANAGER` were `BRANCH_USER` and
 * `BRANCH_ADMIN` until TO-388; ABANCA-facing surfaces still label them
 * "Branch user (Gestor de oficina)" and "Branch manager (Director de oficina)".
 * `ORG_ADMIN` administers a single organization (tenant) as a whole.
 */
export enum Role {
	ORG_USER = 'ORG_USER',
	ORG_MANAGER = 'ORG_MANAGER',
	ORG_ADMIN = 'ORG_ADMIN',
	PLATFORM_ADMIN = 'PLATFORM_ADMIN',
	SUPER_ADMIN = 'SUPER_ADMIN',
}

/**
 * Static role → permission bundles. The seed script (to-58) reconciles the
 * `users_role_permissions` table to match this map on every run, so editing here
 * + redeploying the seed is the supported way to change a built-in role.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
	[Role.ORG_USER]: [
		Permission.CUSTOMER_READ,
		// TO-388: a branch user creates customers — safe only because the principal
		// scope (SELF) confines every customer read and write to their own unit(s).
		Permission.CUSTOMER_WRITE,
		Permission.OFFER_READ,
		// A branch user's core job is creating and sending offers, so the bundle
		// carries OFFER_WRITE; publishing/archiving stays with the branch admin.
		Permission.OFFER_WRITE,
		// Branch users mint and resolve short URLs for the offers they send.
		Permission.SHORT_URL_CREATE,
		Permission.SHORT_URL_RESOLVE,
		Permission.TEMPLATE_READ,
		// Branch users browse the product catalog when building offers.
		Permission.PRODUCT_READ,
		// File storage (TO-324) — see the note on Permission.FILE_READ: the bundle
		// is uniform across roles because per-file ownership does the scoping.
		Permission.FILE_READ,
		Permission.FILE_WRITE,
		Permission.FILE_DELETE,
		// Organization (TO-411): everyone may read the tree they sit in.
		Permission.ORGANIZATION_UNIT_READ,
		Permission.ORGANIZATION_TYPE_READ,
		// Content (TO-419): everyone may read the tenant's controlled texts.
		Permission.CONTENT_READ,
	],
	// A branch manager's bundle (formerly BRANCH_ADMIN). Its customer reach is
	// its unit subtree (default scope SELF_AND_DESCENDANTS, TO-388); user and
	// stats scoping follow in TO-435.
	[Role.ORG_MANAGER]: [
		Permission.CUSTOMER_READ,
		Permission.CUSTOMER_WRITE,
		Permission.CUSTOMER_PII_READ,
		// Customer v2 (TO-418): branch admins reassign and capture consent for the
		// customers of their unit subtree; tenant-wide reads stay with ORG_ADMIN.
		Permission.CUSTOMER_ASSIGN,
		Permission.CUSTOMER_CONSENT_WRITE,
		Permission.OFFER_READ,
		Permission.OFFER_WRITE,
		Permission.OFFER_PUBLISH,
		Permission.SHORT_URL_CREATE,
		Permission.SHORT_URL_RESOLVE,
		Permission.TEMPLATE_READ,
		Permission.AUDIT_READ,
		// Branch admins manage users and roles within their own tenant, and can
		// read the tenant/user directory from the admin console.
		Permission.USER_CREATE,
		Permission.USER_READ,
		Permission.USER_WRITE,
		Permission.ROLE_ASSIGN,
		Permission.TENANT_READ,
		// Branch admins curate the product catalog within their own tenant.
		Permission.PRODUCT_READ,
		Permission.PRODUCT_WRITE,
		// Branch admins author, publish and evaluate prompts, but tenant-wide
		// prompt config (defaults + the eval gate) stays with ORG_ADMIN.
		Permission.PROMPT_READ,
		Permission.PROMPT_WRITE,
		Permission.PROMPT_PUBLISH,
		Permission.PROMPT_EVAL_RUN,
		// File storage (TO-324) — see the note on Permission.FILE_READ: the bundle
		// is uniform across roles because per-file ownership does the scoping.
		Permission.FILE_READ,
		Permission.FILE_WRITE,
		Permission.FILE_DELETE,
		// Organization (TO-411): everyone may read the tree they sit in.
		Permission.ORGANIZATION_UNIT_READ,
		Permission.ORGANIZATION_TYPE_READ,
		// TO-412: user administrators may read who sits where.
		Permission.ORGANIZATION_ASSIGNMENT_READ,
		// Content (TO-419): everyone may read the tenant's controlled texts.
		Permission.CONTENT_READ,
	],
	/**
	 * Organization (tenant) administrator: owns their organization's
	 * configuration and details, its users and role assignments, and its product
	 * catalogue — including creating products. Scoped to their own tenant by the
	 * DB-resolved principal; they never see another tenant's data.
	 */
	[Role.ORG_ADMIN]: [
		Permission.CUSTOMER_READ,
		Permission.CUSTOMER_WRITE,
		Permission.CUSTOMER_PII_READ,
		// Customer v2 (TO-418): tenant-wide visibility, reassignment, consent,
		// partner attributes and GDPR anonymisation.
		Permission.CUSTOMER_READ_ALL,
		Permission.CUSTOMER_ASSIGN,
		Permission.CUSTOMER_CONSENT_WRITE,
		Permission.CUSTOMER_ATTRIBUTES_WRITE,
		Permission.CUSTOMER_ANONYMIZE,
		Permission.OFFER_READ,
		Permission.OFFER_WRITE,
		Permission.OFFER_PUBLISH,
		Permission.OFFER_ARCHIVE,
		Permission.SHORT_URL_CREATE,
		Permission.SHORT_URL_RESOLVE,
		Permission.TEMPLATE_READ,
		Permission.TEMPLATE_WRITE,
		Permission.AUDIT_READ,
		// Org admins manage the users and role assignments inside their tenant.
		Permission.USER_CREATE,
		Permission.USER_READ,
		Permission.USER_WRITE,
		Permission.ROLE_ASSIGN,
		Permission.USER_MANAGE_ROLES,
		Permission.USER_DEACTIVATE,
		Permission.TENANT_READ,
		// Org admins configure their own tenant's SSO (TO-308) — never another's.
		Permission.TENANT_MANAGE_IDP,
		// Org admins open their own tenant's configuration console (TO-374).
		Permission.TENANT_CONFIG_MANAGE,
		// Org admins set their own tenant's content locales (TO-415).
		Permission.TENANT_MANAGE_SETTINGS,
		// Org admins configure their own tenant's file exchange (TO-391).
		Permission.FILE_EXCHANGE_MANAGE,
		// Org admins upload their own tenant's files through api / web (TO-392).
		Permission.FILE_EXCHANGE_UPLOAD,
		// Org admins author and activate their own tenant's import definitions (TO-414).
		Permission.IMPORT_DEFINITION_READ,
		Permission.IMPORT_DEFINITION_MANAGE,
		// Org admins monitor their own tenant's imports (TO-399) — not the original file.
		Permission.IMPORT_EXECUTION_READ,
		// Org admins configure their own tenant's email sender, SMS sending and
		// notification toggles (TO-382, TO-383).
		Permission.NOTIFICATION_SETTINGS_MANAGE,
		// Org admins configure their own tenant's offer-PDF watermark (TO-495).
		Permission.PDF_SETTINGS_MANAGE,
		// Org admins maintain their own tenant's offer notification templates (TO-140).
		Permission.NOTIFICATION_TEMPLATES_MANAGE,
		// Personal API keys (TO-516): an org admin mints, lists and revokes their own.
		Permission.API_KEY_READ,
		Permission.API_KEY_CREATE,
		Permission.API_KEY_REVOKE,
		// Org admins register their own tenant's inbound webhook endpoints and
		// read its delivery ledger (TO-323) — never another tenant's.
		Permission.WEBHOOK_READ,
		Permission.WEBHOOK_MANAGE,
		// Org admins own their tenant's product catalogue end to end.
		Permission.PRODUCT_READ,
		Permission.PRODUCT_WRITE,
		Permission.PRODUCT_DELETE,
		// Org admins own their tenant's prompt library end to end, including the
		// tenant-wide defaults and the eval gate (PROMPT_ADMIN).
		Permission.PROMPT_READ,
		Permission.PROMPT_WRITE,
		Permission.PROMPT_PUBLISH,
		Permission.PROMPT_EVAL_RUN,
		Permission.PROMPT_ADMIN,
		// File storage (TO-324) — see the note on Permission.FILE_READ: the bundle
		// is uniform across roles because per-file ownership does the scoping.
		Permission.FILE_READ,
		Permission.FILE_WRITE,
		Permission.FILE_DELETE,
		// Organization (TO-411): org admins own their tenant's structure and taxonomy.
		Permission.ORGANIZATION_UNIT_READ,
		Permission.ORGANIZATION_TYPE_READ,
		Permission.ORGANIZATION_UNIT_WRITE,
		Permission.ORGANIZATION_TYPE_WRITE,
		// TO-412: org admins place users in units and read who sits where.
		Permission.ORGANIZATION_ASSIGNMENT_READ,
		Permission.ORGANIZATION_ASSIGNMENT_WRITE,
		// Content (TO-419): org admins author, approve (four-eyes), publish and
		// assign the tenant's texts and configure its content types.
		Permission.CONTENT_READ,
		Permission.CONTENT_WRITE,
		Permission.CONTENT_APPROVE,
		Permission.CONTENT_PUBLISH,
		Permission.CONTENT_ASSIGN,
		Permission.CONTENT_TYPES_MANAGE,
		// GDPR compliance (TO-232): org admins answer their own tenant's erasure
		// and access requests and manage its legal holds.
		Permission.COMPLIANCE_READ,
		Permission.COMPLIANCE_ERASURE_MANAGE,
		Permission.COMPLIANCE_LEGAL_HOLD_MANAGE,
		Permission.COMPLIANCE_SAR_MANAGE,
	],
	[Role.SUPER_ADMIN]: Object.values(Permission),
	/**
	 * Read-mostly platform operator (TO-626, ADR-0063): reads across tenants and
	 * holds no cross-tenant write — destructive and configuration actions belong
	 * to SUPER_ADMIN. Its cross-tenant reads are audited like a SUPER_ADMIN's.
	 */
	[Role.PLATFORM_ADMIN]: [
		Permission.TENANT_READ,
		// The `/admin/tenants` read tier and GET-only cross-tenant reads.
		Permission.PLATFORM_TENANT_READ,
		Permission.PLATFORM_AUDIT,
		Permission.AUDIT_READ,
		Permission.AUDIT_EXPORT,
		// Reads the cross-tenant user directory; never provisions or edits users.
		Permission.USER_READ,
		// Views a tenant's SSO/IdP and sign-in-policy configuration.
		Permission.TENANT_IDP_READ,
		// Reads a tenant's inbound webhook ledger while supporting it (TO-323).
		Permission.WEBHOOK_READ,
		Permission.PRODUCT_READ,
		// Views the platform brand catalogue.
		Permission.PLATFORM_BRAND_READ,
		// File storage (TO-324): per-file ownership still scopes the read.
		Permission.FILE_READ,
		// Organization (TO-411): everyone may read the tree they sit in.
		Permission.ORGANIZATION_UNIT_READ,
		Permission.ORGANIZATION_TYPE_READ,
		// TO-412: user administrators may read who sits where.
		Permission.ORGANIZATION_ASSIGNMENT_READ,
		// Content (TO-419): everyone may read the tenant's controlled texts.
		Permission.CONTENT_READ,
	],
}
